import type { Brand } from "./brand.types";

/**
 * Brand key captured from `/login/<brandKey>`. Presentation-only, and always
 * re-checked against the allowlist server-side — never an authorization input.
 *
 * Kept free of `process.env` so the Edge middleware can import it.
 */
export const BRAND_COOKIE_NAME = "alx_brand";

/**
 * Resolved brand payload, so a returning visitor's brand is known synchronously
 * from the cookie. This is what lets the pre-paint inline script set the tab
 * title and favicon with no flicker while pages stay statically prerendered.
 */
export const BRAND_PAYLOAD_COOKIE_NAME = "alx_brand_v1";

/**
 * Written when an organisation resolves to *no* brand, so an unbranded dealer —
 * every dealer today — gets an immediate answer on the next load instead of
 * waiting on a request with a blank logo.
 */
export const BRAND_NONE_SENTINEL = "none";

export const BRAND_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/** Longest brand name we will put in a tab title or splice into copy. */
export const MAX_BRAND_NAME_LENGTH = 64;

/**
 * Hosts allowed to serve brand assets. Brand artwork lives in the per-environment
 * public `static` container, so an allowlist keeps the deploy-free config while
 * making sure a tampered cookie cannot point the favicon or sidebar logo at an
 * attacker's host (a tracking beacon, or a phishing mark).
 */
const ALLOWED_ASSET_HOSTS: ReadonlySet<string> = new Set([
	"alxdev.blob.core.windows.net",
	"alxtest.blob.core.windows.net",
	"alxproduction.blob.core.windows.net",
]);

/**
 * Same-origin paths, or https on an allowlisted host.
 *
 * A protocol-relative URL ("//host/x.svg") starts with "/" but resolves
 * off-origin, and "/\host" is parsed the same way by some browsers — both are
 * rejected before the prefix check can be fooled.
 */
export function isSafeAssetUrl(url: unknown): url is string {
	if (typeof url !== "string" || !url) return false;
	if (/[\r\n\t]/.test(url)) return false;

	if (url.startsWith("//") || url.startsWith("/\\")) return false;
	if (url.startsWith("/")) return true;

	try {
		const parsed = new URL(url);
		return parsed.protocol === "https:" && ALLOWED_ASSET_HOSTS.has(parsed.host);
	} catch {
		return false;
	}
}

export function isValidBrandName(name: unknown): name is string {
	return (
		typeof name === "string" &&
		name.trim().length > 0 &&
		name.length <= MAX_BRAND_NAME_LENGTH &&
		!/[\r\n\t]/.test(name)
	);
}

export function encodeBrandPayload(brand: Brand): string {
	return encodeURIComponent(JSON.stringify(brand));
}

/**
 * The payload cookie is user-writable, so treat it as untrusted input: anything
 * malformed degrades to INFINIT rather than being rendered.
 */
export function decodeBrandPayload(raw: string | undefined): Brand | null {
	if (!raw || raw === BRAND_NONE_SENTINEL) return null;

	try {
		const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<Brand>;
		if (!isValidBrandName(parsed?.displayName)) return null;
		if (
			!isSafeAssetUrl(parsed.wordmarkOnLightUrl) ||
			!isSafeAssetUrl(parsed.wordmarkOnDarkUrl) ||
			!isSafeAssetUrl(parsed.markUrl) ||
			!isSafeAssetUrl(parsed.faviconUrl)
		) {
			return null;
		}
		// Optional in the config, but if the cookie carries one it still ends up in
		// an <img src> — and the resolver never emits an unsafe value, so one here
		// means the cookie was tampered with. Re-resolve rather than trust it.
		if (
			parsed.heroHeadlineUrl !== undefined &&
			!isSafeAssetUrl(parsed.heroHeadlineUrl)
		) {
			return null;
		}
		return parsed as Brand;
	} catch {
		return null;
	}
}

/**
 * Drops both brand cookies. Called on logout: they outlive the session, so the
 * next person to sign in on this browser would otherwise inherit the previous
 * organisation's branding.
 */
export function clearBrandCookies(): void {
	if (typeof document === "undefined") return;
	for (const name of [BRAND_COOKIE_NAME, BRAND_PAYLOAD_COOKIE_NAME]) {
		document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
	}
}

/**
 * Reads the payload cookie in the browser.
 *
 * `known` distinguishes "no cookie yet, we must ask" from "we asked and the
 * answer is INFINIT" — without it an unbranded dealer would blank their logo on
 * every single load waiting for a request whose answer never changes.
 */
export function readBrandCookieState(): {
	known: boolean;
	brand: Brand | null;
} {
	if (typeof document === "undefined") return { known: false, brand: null };

	const match = document.cookie.match(
		new RegExp(`(?:^|;\\s*)${BRAND_PAYLOAD_COOKIE_NAME}=([^;]*)`)
	);
	const raw = match?.[1];
	if (!raw) return { known: false, brand: null };
	if (raw === BRAND_NONE_SENTINEL) return { known: true, brand: null };

	const brand = decodeBrandPayload(raw);
	// A malformed cookie is not an answer — ask again rather than trust it.
	return brand ? { known: true, brand } : { known: false, brand: null };
}
